import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface ParserStatistics {
  date: string;
  total_bills_queued: number;
  total_bills_parsed: number;
  total_bills_failed: number;
  billportal_parses: number;
  openai_fallback_parses: number;
  manual_reviews: number;
  avg_confidence_score: number;
  avg_processing_time_ms: number;
  auth_failures: number;
  auth_success: number;
  auth_not_required: number;
}

export const useParserStatistics = (startDate?: string, endDate?: string) => {
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace;

  return useQuery({
    queryKey: ['parser-statistics', workspace?.id, startDate, endDate],
    queryFn: async () => {
      if (!workspace?.id) return [];

      let query = supabase
        .from('parser_statistics')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('date', { ascending: false });

      if (startDate) {
        query = query.gte('date', startDate);
      }

      if (endDate) {
        query = query.lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ParserStatistics[];
    },
    enabled: !!workspace?.id,
  });
};

export const useParserStatisticsSummary = (days: number = 30) => {
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace;

  return useQuery({
    queryKey: ['parser-statistics-summary', workspace?.id, days],
    queryFn: async () => {
      if (!workspace?.id) return null;

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('parser_statistics')
        .select('*')
        .eq('workspace_id', workspace.id)
        .gte('date', startDate.toISOString().split('T')[0]);

      if (error) throw error;

      // Aggregate statistics
      const stats = data as ParserStatistics[];
      const totals = stats.reduce(
        (acc, stat) => ({
          totalParsed: acc.totalParsed + stat.total_bills_parsed,
          totalFailed: acc.totalFailed + stat.total_bills_failed,
          billportalParses: acc.billportalParses + stat.billportal_parses,
          openaiParses: acc.openaiParses + stat.openai_fallback_parses,
          authFailures: acc.authFailures + stat.auth_failures,
          avgConfidence: acc.avgConfidence + (stat.avg_confidence_score || 0),
          avgProcessingTime: acc.avgProcessingTime + (stat.avg_processing_time_ms || 0),
          daysWithData: acc.daysWithData + 1,
        }),
        {
          totalParsed: 0,
          totalFailed: 0,
          billportalParses: 0,
          openaiParses: 0,
          authFailures: 0,
          avgConfidence: 0,
          avgProcessingTime: 0,
          daysWithData: 0,
        }
      );

      const successRate = totals.totalParsed + totals.totalFailed > 0
        ? (totals.totalParsed / (totals.totalParsed + totals.totalFailed)) * 100
        : 0;

      return {
        ...totals,
        successRate: successRate.toFixed(1),
        avgConfidence: totals.daysWithData > 0 ? (totals.avgConfidence / totals.daysWithData).toFixed(1) : '0',
        avgProcessingTime: totals.daysWithData > 0 ? Math.round(totals.avgProcessingTime / totals.daysWithData) : 0,
      };
    },
    enabled: !!workspace?.id,
  });
};
